import { Add, Delete, Edit, Article, Home, PlayArrow, Refresh, Stop } from "@mui/icons-material";
import {
  Box,
  Breadcrumbs,
  Button,
  CardActions,
  CardContent,
  Checkbox,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";
import { COLORS } from "assets";
import {
  BulkActionBar,
  DataTable,
  KebabMenu,
  PageToolbar,
  SSHReasonModal,
  SiteSelect,
  StatusPill,
  StyledCard,
  bulkCheckboxSx,
  bulkDeleteButtonSx,
  bulkPrimaryButtonSx
} from "components";
import StyledTablePagination from "components/StyledTablePagination";
import { BREAK_GLASS_APPROVED_EVENT, BOX_STATUS_LABELS, ENDPOINTS, TUNNEL_STATUS_LABELS } from "config/constants";
import { useContextController } from "context";
import { useFetch, useTitle } from "hooks";
import useTranslation from "hooks/useTranslation";
import PrivateLayout from "layouts/PrivateLayout";
import { ChangeEventHandler, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { BoxModel, MILLISECONDS } from "types";
import { canManageAnySite, canManageSite, ensureBreakGlassForSite } from "utils";
import EditBox from "./[id]";
import AddBox from "./add";
import BulkRemoveBoxes from "./bulk-remove";
import BulkManageBoxes from "./manage-bulk";
import RemoveBox from "./remove";

interface Data {
  boxes?: BoxModel[];
  totalRecords?: number;
}

interface IBoxRowActions {
  box: BoxModel;
  onEdit: (box: BoxModel) => void;
  onRemove: (box: BoxModel) => void;
}

function BoxRowActions({ box, onEdit, onRemove }: IBoxRowActions) {
  const t = useTranslation();
  const navigate = useNavigate();
  const { isLoading, response, error, callFetch } = useFetch("POST");
  const [sshAction, setSshAction] = useState<"start" | "stop" | null>(null);

  const onSSH = useCallback(
    (start: boolean, reason: string) => {
      callFetch(ENDPOINTS.boxes + `/${box._id}/ssh`, {
        body: JSON.stringify({ start, reason })
      }).catch(console.error);
    },
    [box, callFetch]
  );

  useEffect(() => {
    if (response) {
      toast.success(response.message || "");
      setSshAction(null);
    }
  }, [response]);
  useEffect(() => {
    if (error) toast.error(error.message || "");
  }, [error]);

  return (
    <>
      {sshAction ? (
        <SSHReasonModal
          open
          action={sshAction}
          loading={isLoading}
          onClose={() => !isLoading && setSshAction(null)}
          onConfirm={(reason) => onSSH(sshAction === "start", reason)}
        />
      ) : null}
      <KebabMenu
        items={[
        {
          key: "logs",
          label: t["Logs"],
          icon: <Article fontSize="small" />,
          onClick: () => {
            navigate("/boxes/" + box._id + "/logs");
          }
        },
        {
          key: "ssh-start",
          label: t["Start SSH"],
          icon: <PlayArrow fontSize="small" />,
          disabled: isLoading,
          onClick: () => setSshAction("start")
        },
        {
          key: "ssh-stop",
          label: t["Stop SSH"],
          icon: <Stop fontSize="small" />,
          disabled: isLoading,
          onClick: () => setSshAction("stop")
        },
        {
          key: "edit",
          label: t["Edit"],
          icon: <Edit fontSize="small" />,
          onClick: () => onEdit(box)
        },
        {
          key: "delete",
          label: t["Delete"],
          icon: <Delete fontSize="small" />,
          destructive: true,
          onClick: () => onRemove(box)
        }
      ]}
      />
    </>
  );
}

export default function Boxes() {
  const [{ userData }] = useContextController();
  const canAdd = canManageAnySite(userData);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<BoxModel>();
  const [removing, setRemoving] = useState<BoxModel>();
  const [bulkManage, setBulkManage] = useState(false);
  const [bulkRemove, setBulkRemove] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [siteId, setSiteId] = useState<string>(() => localStorage.getItem("boxListSiteId") || "");
  const [reload, setReload] = useState(false);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<keyof BoxModel>();
  const [isAscending, setAscending] = useState(false);

  const t = useTranslation();
  const { isLoading, response, error, callFetch: callFetchGet } = useFetch<Data>("GET");
  const { boxes, totalRecords } = useMemo(() => {
    const data = response && response.data;
    if (data) {
      return data;
    }
    return {};
  }, [response]);
  const searchWaitRef = useRef<NodeJS.Timeout>();
  const onSort = useCallback(
    (val: keyof BoxModel) => {
      if (val === sort) {
        if (isAscending) {
          setAscending(false);
        } else {
          setSort(undefined);
        }
      } else {
        setSort(val);
        setAscending(true);
      }
    },
    [sort, isAscending]
  );
  const onReload = useCallback(() => setReload((prevState) => !prevState), []);
  const onPageChange = (_: any, page: number) => setPage(page);
  const onRowsPerPageChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = ({ target: { value } }) => {
    setLimit(Number.parseInt(value, 10));
    setPage(0);
  };

  useEffect(() => {
    if (!siteId) return;
    let params = `?limit=${limit}&page=${page + 1}&siteId=${siteId}`;
    if (search) {
      params += `&search=${search}`;
    }
    if (sort) {
      if (isAscending) {
        params += `&sort=${sort}`;
      } else {
        params += `&sort=-${sort}`;
      }
    }
    if (searchWaitRef.current) {
      clearTimeout(searchWaitRef.current);
    }
    searchWaitRef.current = setTimeout(() => {
      callFetchGet(ENDPOINTS.boxes + params).catch(console.error);
    }, 300);
  }, [page, limit, search, sort, isAscending, reload, callFetchGet, siteId]);

  // Refresh tunnel/box status while the list is open (cron updates tunnelStatus about every minute).
  useEffect(() => {
    if (!siteId) return;
    const timer = setInterval(() => {
      setReload((prev) => !prev);
    }, MILLISECONDS.SECOND * 30);
    return () => clearInterval(timer);
  }, [siteId]);

  useEffect(() => setPage(0), [search, siteId]);
  useEffect(() => setSelectedIds([]), [page, limit, search, sort, isAscending, siteId]);

  useTitle("Boxes");

  const canBulk = canManageSite(userData, siteId);
  const manageableIds = useMemo(
    () =>
      (boxes || [])
        .filter((box) => canManageSite(userData, box.siteId || box.site?._id))
        .map((box) => box._id),
    [boxes, userData]
  );
  const selectedBoxes = useMemo(
    () => (boxes || []).filter((box) => selectedIds.includes(box._id)),
    [boxes, selectedIds]
  );
  const allManageableSelected =
    manageableIds.length > 0 && manageableIds.every((id) => selectedIds.includes(id));
  const someManageableSelected = manageableIds.some((id) => selectedIds.includes(id));

  const toggleAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (manageableIds.every((id) => prev.includes(id))) {
        return prev.filter((id) => !manageableIds.includes(id));
      }
      return Array.from(new Set([...prev, ...manageableIds]));
    });
  }, [manageableIds]);

  const toggleOne = useCallback((id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  }, []);

  const columns = useMemo(
    () => [
      ...(canBulk
        ? [
            {
              header: (
                <Checkbox
                  size="small"
                  checked={allManageableSelected}
                  indeterminate={someManageableSelected && !allManageableSelected}
                  onChange={toggleAll}
                  disabled={!manageableIds.length}
                  sx={bulkCheckboxSx}
                />
              ) as unknown as string,
              align: "center" as const,
              accessor: (row: BoxModel) => {
                const manage = canManageSite(userData, row.siteId || row.site?._id);
                if (!manage) return null;
                return (
                  <Checkbox
                    size="small"
                    checked={selectedIds.includes(row._id)}
                    onChange={() => toggleOne(row._id)}
                    sx={bulkCheckboxSx}
                  />
                );
              }
            }
          ]
        : []),
      {
        header: t["ID"],
        accessor: "boxId" as const
      },
      {
        header: t["Device ID"],
        accessor: "deviceId" as const,
        sort: "deviceId" as const
      },
      {
        header: t["Name"],
        accessor: "name" as const,
        sort: "name" as const
      },
      {
        header: t["Site"],
        accessor: (row: BoxModel) => row.site?.name
      },
      {
        header: t["Status"],
        align: "center" as const,
        accessor: (row: BoxModel) => {
          const status = row.boxStatus || "off";
          const tunnel = row.tunnelStatus || "unknown";
          return (
            <Stack direction="row" spacing={0.75} justifyContent="center" alignItems="center" flexWrap="wrap" useFlexGap>
              <StatusPill label={BOX_STATUS_LABELS[status]} status={status} />
              {tunnel !== "unknown" ? (
                <StatusPill label={TUNNEL_STATUS_LABELS[tunnel]} status={tunnel} />
              ) : null}
            </Stack>
          );
        },
        sort: "boxStatus" as const
      },
      ...(canManageAnySite(userData)
        ? [
            {
              header: t["Actions"],
              align: "center" as const,
              accessor: (row: BoxModel) => {
                const manage = canManageSite(userData, row.siteId || row.site?._id);
                return manage ? (
                  <Stack direction="row" justifyContent="center" alignItems="center">
                    <BoxRowActions box={row} onEdit={setEditing} onRemove={setRemoving} />
                  </Stack>
                ) : null;
              }
            }
          ]
        : [])
    ],
    [
      t,
      userData,
      canBulk,
      allManageableSelected,
      someManageableSelected,
      manageableIds.length,
      selectedIds,
      toggleAll,
      toggleOne
    ]
  );

  return (
    <PrivateLayout canAccess={() => true}>
      {canAdd && adding && <AddBox onSuccess={onReload} onClose={() => setAdding(false)} />}
      {editing && canManageSite(userData, editing.siteId || editing.site?._id) && (
        <EditBox box={editing} onSuccess={onReload} onClose={() => setEditing(undefined)} />
      )}
      {removing && canManageSite(userData, removing.siteId || removing.site?._id) && (
        <RemoveBox removing={removing} onSuccess={onReload} onClose={() => setRemoving(undefined)} />
      )}
      {bulkManage && selectedBoxes.length > 0 && (
        <BulkManageBoxes
          boxes={selectedBoxes}
          onSuccess={() => {
            setSelectedIds([]);
            onReload();
          }}
          onClose={() => setBulkManage(false)}
        />
      )}
      {bulkRemove && selectedBoxes.length > 0 && (
        <BulkRemoveBoxes
          boxes={selectedBoxes}
          onSuccess={() => {
            setSelectedIds([]);
            onReload();
          }}
          onClose={() => setBulkRemove(false)}
        />
      )}

      <Box width="100%" display="flex" alignItems="center" justifyContent="space-between" mb={2} gap={2} flexWrap="wrap">
        <Breadcrumbs aria-label="breadcrumb">
          <Tooltip title={t["Home"]}>
            <IconButton LinkComponent={Link} {...{ to: "/" }} size="small" sx={{ color: COLORS.surface.secondaryText }}>
              <Home fontSize="small" />
            </IconButton>
          </Tooltip>
          <Typography color="text.primary">{t["Boxes"]}</Typography>
        </Breadcrumbs>
        <Box sx={{ width: { xs: "100%", sm: 300 }, maxWidth: "100%" }}>
          <SiteSelect
            value={siteId || undefined}
            hideLabel
            disableAutoSelect={userData?.type === "super"}
            onChange={(id) => {
              if (!ensureBreakGlassForSite(userData?.type, id)) {
                const onApproved = (event: Event) => {
                  const approved = (event as CustomEvent<{ siteId?: string }>).detail?.siteId;
                  if (String(approved) === String(id)) {
                    setSiteId(id);
                    localStorage.setItem("boxListSiteId", id);
                    window.removeEventListener(BREAK_GLASS_APPROVED_EVENT, onApproved);
                  }
                };
                window.addEventListener(BREAK_GLASS_APPROVED_EVENT, onApproved);
                return;
              }
              setSiteId(id);
              if (id) localStorage.setItem("boxListSiteId", id);
            }}
          />
        </Box>
      </Box>

      <PageToolbar
        title={t["Surveillance Boxes"]}
        search={search}
        searchPlaceholder={t["Search boxes by device id."]}
        onSearchChange={setSearch}
        actions={
          <>
            {canAdd ? (
              <Tooltip title={t["Add Box"]}>
                <IconButton
                  onClick={() => setAdding(true)}
                  sx={{
                    bgcolor: COLORS.surface.card,
                    color: COLORS.surface.primaryText,
                    border: `1px solid ${COLORS.surface.border}`,
                    borderRadius: `${COLORS.radius.sm}px`,
                    "&:hover": { bgcolor: COLORS.surface.tableHeader }
                  }}
                >
                  <Add />
                </IconButton>
              </Tooltip>
            ) : null}
            {isLoading ? (
              <IconButton
                disabled
                sx={{
                  width: 40,
                  height: 40,
                  color: COLORS.surface.primaryText,
                  border: `1px solid ${COLORS.surface.border}`,
                  bgcolor: COLORS.surface.card,
                  borderRadius: `${COLORS.radius.sm}px`
                }}
              >
                <CircularProgress size={20} sx={{ color: COLORS.primary }} />
              </IconButton>
            ) : (
              <Tooltip title={t["Refresh"]}>
                <IconButton
                  onClick={onReload}
                  sx={{
                    bgcolor: COLORS.surface.card,
                    color: COLORS.surface.primaryText,
                    border: `1px solid ${COLORS.surface.border}`,
                    borderRadius: `${COLORS.radius.sm}px`,
                    "&:hover": { bgcolor: COLORS.surface.tableHeader }
                  }}
                >
                  <Refresh />
                </IconButton>
              </Tooltip>
            )}
          </>
        }
      />

      <Box width="100%">
        <StyledCard>
          <CardContent sx={{ pt: 2 }}>
            <BulkActionBar
              count={selectedIds.length}
              label={selectedIds.length === 1 ? "box selected" : "boxes selected"}
              onClear={() => setSelectedIds([])}
              clearLabel={t["Clear Selection"] || "Clear Selection"}
            >
              <Button variant="contained" onClick={() => setBulkManage(true)} sx={bulkPrimaryButtonSx}>
                {t["Manage Recording"] || "Manage Recording"}
              </Button>
              {canBulk ? (
                <Button variant="contained" onClick={() => setBulkRemove(true)} sx={bulkDeleteButtonSx}>
                  {t["Delete"]}
                </Button>
              ) : null}
            </BulkActionBar>
            {!siteId ? (
              <Typography sx={{ color: COLORS.surface.secondaryText, py: 4, textAlign: "center" }}>
                {t["Select a site to view boxes."] || "Select a site to view boxes."}
              </Typography>
            ) : (
              <DataTable
                rows={boxes || null}
                isLoading={isLoading}
                error={error}
                sort={sort}
                isAscending={isAscending}
                onSort={onSort}
                columns={columns}
                selectedIds={selectedIds}
                getRowId={(row) => row._id}
              />
            )}
          </CardContent>
          <CardActions sx={{ justifyContent: "flex-end" }}>
            <StyledTablePagination
              component="div"
              align="right"
              count={totalRecords || 0}
              page={page}
              onPageChange={onPageChange}
              rowsPerPage={limit}
              onRowsPerPageChange={onRowsPerPageChange}
            />
          </CardActions>
        </StyledCard>
      </Box>
    </PrivateLayout>
  );
}
